import { isIntranetHtmlBody } from '@gb-ticket/shared-types';
import { type KeyboardEvent, type MouseEvent, useLayoutEffect, useRef, useState } from 'react';

import { IntranetImageLightbox } from '@/products/intranet/components/intranet-image-frame';
import { useColleagueProfile } from '@/products/intranet/features/intranet/colleague-profile-context';
import { frameIntranetBodyImages } from '@/products/intranet/features/intranet/image-frame';

/**
 * Pasted mail often joins words with non-breaking spaces. Those spaces do not wrap,
 * so a line breaks inside a word to stay in the column. Normal spaces wrap between words.
 */
function wrapOnSpaces(value: string): string {
  return value.replace(/&nbsp;|&#160;|&#xa0;/gi, ' ').replaceAll('\u00A0', ' ');
}

/**
 * The one renderer for content bodies. Editor bodies are HTML the server has already reduced to its
 * allowlist; older bodies without a leading tag stay plain text, so their markup is never parsed.
 * Mentions (`span[data-user-id]`) open the colleague sheet; they are not links.
 * Images stay inside the 680 preview. Only an article (`expandImages`) opens the original.
 */
export function IntranetBody({
  body,
  expandImages = false,
  phrase,
  sectionOrder,
}: {
  body: string | null;
  expandImages?: boolean;
  /** Phrase to mark in one Abschnitt. Omitted, the text stays as stored. */
  phrase?: string;
  /**
   * Heading order from 1. 0 is the text before the first heading, or the whole text when
   * the page has no heading. Omitted, nothing is marked.
   */
  sectionOrder?: number;
}) {
  const { open } = useColleagueProfile();
  const rootRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<{ src: string; alt: string } | null>(null);
  const readable = body ? wrapOnSpaces(body) : body;
  const html = readable && isIntranetHtmlBody(readable) ? readable : null;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !html) {
      return;
    }
    return frameIntranetBodyImages(root, expandImages);
  }, [html, expandImages]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !phrase || sectionOrder === undefined) {
      return;
    }
    const targets = sectionElements(root, sectionOrder);
    if (!targets) {
      return;
    }
    markPhrase(targets, phrase)?.scrollIntoView({ block: 'start' });
  }, [html, phrase, readable, sectionOrder]);

  if (!body?.trim()) {
    return null;
  }

  function openPhoto(photo: HTMLImageElement) {
    setZoom({ src: photo.currentSrc || photo.src, alt: photo.alt });
  }

  function onClick(event: MouseEvent<HTMLDivElement>) {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const mention = target.closest('span[data-user-id]');
    if (mention instanceof HTMLElement) {
      const userId = mention.getAttribute('data-user-id')?.trim();
      if (!userId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      open(userId);
      return;
    }
    if (!expandImages) {
      return;
    }
    const frame = target.closest('.intranet-image-frame');
    const photo = frame?.querySelector('img.intranet-image-photo');
    if (!(photo instanceof HTMLImageElement)) {
      return;
    }
    event.preventDefault();
    openPhoto(photo);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!expandImages || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    const target = event.target;
    if (
      !(target instanceof HTMLImageElement) ||
      !target.classList.contains('intranet-image-photo')
    ) {
      return;
    }
    event.preventDefault();
    openPhoto(target);
  }

  if (html) {
    return (
      <>
        <div
          ref={rootRef}
          className={
            expandImages
              ? 'intranet-prose intranet-image-expand text-base leading-relaxed'
              : 'intranet-prose text-base leading-relaxed'
          }
          dangerouslySetInnerHTML={{ __html: html }}
          onClick={onClick}
          onKeyDown={onKeyDown}
        />
        {zoom ? (
          <IntranetImageLightbox src={zoom.src} alt={zoom.alt} onClose={() => setZoom(null)} />
        ) : null}
      </>
    );
  }
  return (
    <div ref={rootRef} className="text-base leading-relaxed whitespace-pre-wrap">
      {readable}
    </div>
  );
}

function sectionElements(root: HTMLElement, order: number): Element[] | null {
  const headings = [...root.querySelectorAll('h2, h3')];
  if (order === 0) {
    if (headings.length === 0) {
      return [root];
    }
    const first = headings[0];
    const nodes: Element[] = [];
    for (const child of root.children) {
      if (child === first || (first && child.contains(first))) {
        break;
      }
      nodes.push(child);
    }
    return nodes;
  }
  const heading = headings[order - 1];
  if (!heading) {
    return null;
  }
  const nodes: Element[] = [heading];
  let next = heading.nextElementSibling;
  while (next && !/^H[23]$/.test(next.tagName)) {
    nodes.push(next);
    next = next.nextElementSibling;
  }
  return nodes;
}

function markPhrase(nodes: readonly Element[], phrase: string): HTMLElement | null {
  const texts: Text[] = [];
  for (const node of nodes) {
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    let current = walker.nextNode();
    while (current) {
      if (!(current as Text).parentElement?.closest('mark')) {
        texts.push(current as Text);
      }
      current = walker.nextNode();
    }
  }
  const spans: { node: Text; start: number; end: number }[] = [];
  let cursor = 0;
  for (const node of texts) {
    const start = cursor;
    cursor += node.data.length;
    spans.push({ node, start, end: cursor });
  }
  const pattern = new RegExp(escapeRegExp(phrase), 'gi');
  const joined = texts.map((node) => node.data).join('');
  const found = [...joined.matchAll(pattern)].filter((match) => match[0].length > 0);
  if (found.length === 0) {
    return null;
  }
  const slices = new Map<Text, { localStart: number; localEnd: number }[]>();
  for (const match of found) {
    const from = match.index ?? 0;
    const to = from + match[0].length;
    for (const span of spans) {
      const localStart = Math.max(from, span.start) - span.start;
      const localEnd = Math.min(to, span.end) - span.start;
      if (localStart >= localEnd) {
        continue;
      }
      const list = slices.get(span.node) ?? [];
      list.push({ localStart, localEnd });
      slices.set(span.node, list);
    }
  }
  let first: HTMLElement | null = null;
  for (const span of spans) {
    const pieces = slices.get(span.node);
    if (!pieces || pieces.length === 0) {
      continue;
    }
    pieces.sort((left, right) => left.localStart - right.localStart);
    const fragment = document.createDocumentFragment();
    let last = 0;
    let nodeFirst: HTMLElement | null = null;
    for (const piece of pieces) {
      fragment.append(span.node.data.slice(last, piece.localStart));
      const mark = document.createElement('mark');
      mark.textContent = span.node.data.slice(piece.localStart, piece.localEnd);
      nodeFirst ??= mark;
      fragment.append(mark);
      last = piece.localEnd;
    }
    fragment.append(span.node.data.slice(last));
    span.node.replaceWith(fragment);
    first ??= nodeFirst;
  }
  return first;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
